import { queryOptions, mutationOptions } from '@tanstack/react-query';
import {
	getNgoProjectsFn,
	getNgoProjectFn,
	putNgoProjectFn,
	deleteNgoProjectFn,
	duplicateNgoProjectFn,
	launchNgoProjectFn,
	pauseNgoProjectFn,
	unpauseNgoProjectFn,
	getNgoPortalsFn,
	getNgoPortalFn,
	updateNgoPortalFn,
	getNgoProfileFn,
	putNgoProfileFn,
	getProjectCollectedFn,
} from './server-functions-ngo';
import {
	requestNgoLoginFn,
	logoutNgoFn,
	validateMagicLinkFn,
	impersonateNgoFn,
	listAllNgosFn,
} from './server-functions-auth';
import { createOnboardingContractFn, downloadDraftContractFn } from './server-functions-contract';
import { createOnboardingDraftFn } from './server-functions-onboarding-draft';
import { getOnboardingTermsFn } from './server-functions-onboarding-terms';
import { translateProjectTextsFn } from './server-functions-translate';
import { useSuspenseQuery } from '@tanstack/react-query';
import { notFound, redirect } from '@tanstack/react-router';
import type { Language, TranslatableText } from '@worldcoo/monolith/core';
import { useDeferredValue } from 'react';

function redirectToLogin(): never {
	const currentPath =
		typeof window !== 'undefined' ? window.location.pathname + window.location.search + window.location.hash : '';
	// Strip /ngos prefix so redirect is relative to /ngos (callback/impersonate pages prepend it back)
	const relativePath = currentPath.startsWith('/ngos') ? currentPath.slice('/ngos'.length) : currentPath;
	throw redirect({
		to: '/ngos/login',
		search: { redirect: relativePath || undefined },
	});
}

/**
 * MARK: getNgoProjects
 */
export const ngoProjectsOptions = ({
	query,
	fundingType,
	status,
}: {
	query: string | undefined;
	fundingType: 'myPortal' | 'worldcoo' | 'both' | undefined;
	status: 'draft' | 'active' | 'paused' | 'funded' | undefined;
}) =>
	queryOptions({
		queryKey: ['ngo', 'projects', query, fundingType, status],
		queryFn: () => getNgoProjectsFn({ data: { query, fundingType, status } }),
	});
export function useNgoProjectsQuery(filters: {
	q: string | undefined;
	fundingType: 'myPortal' | 'worldcoo' | 'both' | undefined;
	status: 'draft' | 'active' | 'paused' | 'funded' | undefined;
}) {
	const deferredFilters = useDeferredValue({
		query: filters.q,
		fundingType: filters.fundingType,
		status: filters.status,
	});
	const { data } = useSuspenseQuery(ngoProjectsOptions(deferredFilters));
	if ('unauthenticated' in data) redirectToLogin();
	return data;
}

/**
 * MARK: putNgoProject (create or update)
 */
export const putNgoProjectOptions = () =>
	mutationOptions({
		mutationKey: ['putNgoProject'],
		mutationFn: (input: { project: Parameters<typeof putNgoProjectFn>[0]['data']['project'] }) =>
			putNgoProjectFn({ data: input }),
	});
export const createNgoProjectOptions = putNgoProjectOptions;
export const updateNgoProjectOptions = putNgoProjectOptions;

/**
 * MARK: getNgoProject
 */
export const ngoProjectOptions = ({ projectId }: { projectId: string }) =>
	queryOptions({
		queryKey: ['ngo', 'project', projectId],
		queryFn: () => getNgoProjectFn({ data: { projectId } }),
	});
export function useNgoProjectQuery(projectId: string) {
	const { data } = useSuspenseQuery(ngoProjectOptions({ projectId }));
	if ('unauthenticated' in data) redirectToLogin();
	if ('notFound' in data) throw notFound();
	return data;
}

/**
 * MARK: getProjectCollected
 */
export const projectCollectedOptions = ({ projectId }: { projectId: string }) =>
	queryOptions({
		queryKey: ['project-collected', projectId],
		queryFn: () => getProjectCollectedFn({ data: { projectId } }),
	});

export const deleteNgoProjectOptions = () =>
	mutationOptions({
		mutationKey: ['deleteNgoProject'],
		mutationFn: (input: { projectId: string }) => deleteNgoProjectFn({ data: input }),
	});

export const duplicateNgoProjectOptions = () =>
	mutationOptions({
		mutationKey: ['duplicateNgoProject'],
		mutationFn: (input: { projectId: string; name: TranslatableText; internalName: string }) =>
			duplicateNgoProjectFn({ data: input }),
	});

export const launchNgoProjectOptions = () =>
	mutationOptions({
		mutationKey: ['launchNgoProject'],
		mutationFn: (input: { projectId: string }) => launchNgoProjectFn({ data: input }),
	});

export const pauseNgoProjectOptions = () =>
	mutationOptions({
		mutationKey: ['pauseNgoProject'],
		mutationFn: (input: { projectId: string }) => pauseNgoProjectFn({ data: input }),
	});

export const unpauseNgoProjectOptions = () =>
	mutationOptions({
		mutationKey: ['unpauseNgoProject'],
		mutationFn: (input: { projectId: string }) => unpauseNgoProjectFn({ data: input }),
	});

/**
 * MARK: NGO Profile
 */
export const ngoProfileOptions = () =>
	queryOptions({
		queryKey: ['ngo', 'profile'],
		queryFn: () => getNgoProfileFn(),
	});

export function useNgoProfileQuery() {
	const { data } = useSuspenseQuery(ngoProfileOptions());
	if ('unauthenticated' in data) redirectToLogin();
	if ('notFound' in data) throw notFound();
	return data;
}

export const putNgoProfileOptions = () =>
	mutationOptions({
		mutationKey: ['putNgoProfile'],
		mutationFn: (input: { data: Record<string, unknown> }) => putNgoProfileFn({ data: input }),
	});

/**
 * MARK: getNgoPortals
 */
export const ngoPortalsOptions = () =>
	queryOptions({
		queryKey: ['ngo', 'portals'],
		queryFn: () => getNgoPortalsFn(),
	});

export function useNgoPortalsQuery() {
	const { data } = useSuspenseQuery(ngoPortalsOptions());
	if ('unauthenticated' in data) redirectToLogin();
	return data;
}

/**
 * MARK: getNgoPortal (single portal detail)
 */
export const ngoPortalOptions = (portalId: string) =>
	queryOptions({
		queryKey: ['ngo', 'portal', portalId],
		queryFn: () => getNgoPortalFn({ data: { portalId } }),
	});

export function useNgoPortalQuery(portalId: string) {
	const { data } = useSuspenseQuery(ngoPortalOptions(portalId));
	if ('unauthenticated' in data) redirectToLogin();
	if ('notFound' in data) throw notFound();
	return data;
}

/**
 * MARK: updateNgoPortal
 */
export const updateNgoPortalOptions = () =>
	mutationOptions({
		mutationKey: ['updateNgoPortal'],
		mutationFn: (input: {
			portalId: string;
			title?: string;
			presentation?: string;
			themeBaseColor?: string;
			themeSecondaryColor?: string;
			amountOptions?: Array<Record<string, number>>;
			logoImage?: string;
			coverImage?: string;
			languages?: Language[];
			textsCollection?: {
				langCode: Language;
				presentation?: string;
				title?: string;
				imageAlt?: string;
				donateButton?: string;
				faqItems?: { question: string; answer: string }[];
				amountEquivalents?: string[];
			}[];
			sliderItems?: {
				texts: { langCode: Language; quote: string; authorName: string; imageAlt?: string }[];
			}[];
			sliderImages?: { index: number; image: string }[];
		}) => updateNgoPortalFn({ data: input }),
	});

/**
 * MARK: NGO Auth
 */
export const requestNgoLoginOptions = () =>
	mutationOptions({
		mutationKey: ['requestNgoLogin'],
		mutationFn: (input: { email: string; locale?: string; captchaToken: string; redirect?: string }) =>
			requestNgoLoginFn({ data: input }),
	});

export const logoutNgoOptions = () =>
	mutationOptions({
		mutationKey: ['logoutNgo'],
		mutationFn: () => logoutNgoFn(),
	});

export const validateMagicLinkOptions = () =>
	mutationOptions({
		mutationKey: ['validateMagicLink'],
		mutationFn: (input: { token: string }) => validateMagicLinkFn({ data: input }),
	});

export const impersonateNgoOptions = () =>
	mutationOptions({
		mutationKey: ['impersonateNgo'],
		mutationFn: (input: { email: string }) => impersonateNgoFn({ data: input }),
	});

export const listAllNgosOptions = () =>
	queryOptions({
		queryKey: ['listAllNgos'],
		queryFn: () => listAllNgosFn(),
	});

/**
 * MARK: Onboarding
 */
export const createOnboardingDraftOptions = () =>
	mutationOptions({
		mutationKey: ['createOnboardingDraft'],
		mutationFn: (input: { data: Record<string, unknown>; captchaToken: string }) =>
			createOnboardingDraftFn({ data: input }),
	});

export const createOnboardingContractOptions = () =>
	mutationOptions({
		mutationKey: ['createOnboardingContract'],
		mutationFn: (input: {
			data: {
				legalId: string;
				legalName: string;
				ngoName: string;
				legalEmail: string;
				urlProtection: string;
				beneficiariName: string;
				SWIFT: string;
				IBAN: string;
				bankAccountNumber: string;
				ngoTaxId: string;
				ngoAddress: string;
				nowDate: string;
			};
			locale: 'en' | 'es' | 'ca' | 'it';
			captchaToken: string;
		}) => createOnboardingContractFn({ data: input }),
	});

export const downloadDraftContractOptions = () =>
	mutationOptions({
		mutationKey: ['downloadDraftContract'],
		mutationFn: () => downloadDraftContractFn(),
	});

export const onboardingTermsOptions = (locale: 'en' | 'es' | 'ca' | 'it') =>
	queryOptions({
		queryKey: ['onboarding-terms', locale],
		queryFn: () => getOnboardingTermsFn({ data: locale }),
	});

/**
 * MARK: translateProjectTexts
 */
export const translateProjectTextsOptions = () =>
	mutationOptions({
		mutationKey: ['translateProjectTexts'],
		mutationFn: (input: {
			sourceLanguage: Language;
			targetLanguages: Language[];
			texts: Record<string, string>;
			captchaToken: string;
		}) => translateProjectTextsFn({ data: input }),
	});
